import { Controller } from "@hotwired/stimulus";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class extends Controller {
  static targets = ["cover", "coverImageSingle", "coverImagePrimary", "coverImageSecondary", "centerImage", "contentSection", "content", "headerBottom"];

  connect() {
    this.tweens = [];
    this.scrollTriggers = [];
    this._initializeCoverFade();
    this._initializeCoverImageBlur();
    this._initializeHeaderContentFade();
    this._initializeHeaderContentSectionPosition();
    this._initializeMenuTransparencyToggle();
    this._initializeContentLayout();
    this._initializeCenterImage();
  }

  disconnect() {
    this.tweens?.forEach((tween) => {
      tween.scrollTrigger?.kill();
      tween.kill();
    });
    this.tweens = [];
    this.scrollTriggers?.forEach((trigger) => trigger.kill());
    this.scrollTriggers = [];
    this._layoutObserver?.disconnect();
    if (this._layoutUpdate) {
      window.removeEventListener("load", this._layoutUpdate);
      window.removeEventListener("resize", this._layoutUpdate);
      window.removeEventListener("orientationchange", this._layoutUpdate);
    }
    this._centerImagePushdown = 0;
    if (this.hasContentSectionTarget) {
      this.contentSectionTarget.style.paddingTop = "";
      this.contentSectionTarget.style.paddingBottom = "";
      this.contentSectionTarget.style.position = "";
      this.contentSectionTarget.style.marginTop = "";
    }
  }

  // Fade the whole fixed cover (dark background + images) out as the hero scrolls
  // past, so it doesn't keep covering whatever sits behind it once out of view.
  _initializeCoverFade() {
    if (!this.hasCoverTarget) return;

    this.tweens.push(
      gsap.to(this.coverTarget, {
        opacity: 0,
        ease: "sine.inOut",
        scrollTrigger: {
          trigger: this.element,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      }),
    );
  }

  _initializeCoverImageBlur() {
    if (this.hasCoverImageSingleTarget) {
      this.tweens.push(
        gsap.to(this.coverImageSingleTarget, {
          filter: "blur(20px)",
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "top top",
            end: "50% top",
            scrub: true,
          },
        })
      );
    }

    if (this.hasCoverImagePrimaryTarget) {
      this.tweens.push(
        gsap.to(this.coverImagePrimaryTarget, {
          filter: "blur(20px)",
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "top top",
            end: "50% top",
            scrub: true,
          },
        }),
        gsap.to(this.coverImagePrimaryTarget, {
          yPercent: -10,
          scale: 0.85,
          opacity: 0,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        }),
      );
    }

    if (this.hasCoverImageSecondaryTarget) {
      this.tweens.push(
        gsap.to(this.coverImageSecondaryTarget, {
          filter: "blur(20px)",
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "5% top",
            end: "bottom top",
            scrub: true,
          },
        }),
        gsap.to(this.coverImageSecondaryTarget, {
          yPercent: -20,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        }),
      );
    }
  }

  _initializeCenterImage() {
    if (!this.hasCenterImageTarget) return;

    this.tweens.push(
      gsap.fromTo(
        this.centerImageTarget,
        { y: () => this._centerImagePushdown || 0 },
        {
          y: () => (this._centerImagePushdown || 0) + 100,
          ease: "sine.inOut",
          scrollTrigger: {
            trigger: this.element,
            start: "top top",
            end: "25% top",
            scrub: true,
            invalidateOnRefresh: true,
          },
        },
      ),
      gsap.to(this.centerImageTarget, {
        opacity: 0,
        ease: "sine.inOut",
        scrollTrigger: {
          trigger: this.element,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      }),
    );
  }

  _initializeHeaderContentFade() {
    if (!this.hasContentTarget) return;

    this.tweens.push(
      gsap.to(this.contentTarget, {
        opacity: 0,
        ease: "sine.inOut",
        scrollTrigger: {
          trigger: this.element,
          start: "top top",
          end: "75% top",
          scrub: true,
        },
      }),
    );
  }

  _initializeHeaderContentSectionPosition() {
    if (!this.hasContentSectionTarget || !this.hasContentTarget) return;

    this.scrollTriggers.push(
      ScrollTrigger.create({
        trigger: this.element,
        // Switch the moment the marquee bar (headerBottom) would touch
        // the bottom of the content. Computed in pixels of scroll position.
        start: () => {
          if (!this.hasHeaderBottomTarget) return "15% top";
          const headerBottomRect = this.headerBottomTarget.getBoundingClientRect();
          const contentRect = this.contentTarget.getBoundingClientRect();
          return Math.max(0, headerBottomRect.top - contentRect.bottom);
        },
        invalidateOnRefresh: true,
        // Always measure `start` from the clean (fixed) layout. GSAP does not reset
        // the inline styles we manage ourselves, so without this a refresh that
        // happens while scrolled past the switch point would measure the content in
        // its shifted position and compute a wrong `start` — leaving it stuck.
        onRefreshInit: () => this._pinContentSection(),
        // Reconcile the pin state with the actual scroll position after every refresh,
        // so the content can never end up in the wrong state regardless of missed callbacks.
        onRefresh: (self) => (self.progress > 0 ? this._releaseContentSection() : this._pinContentSection()),
        // Release the pin so the content scrolls away with the header.
        onEnter: () => this._releaseContentSection(),
        onEnterBack: () => this._releaseContentSection(),
        // Re-pin when scrolling back up past the switch point.
        onLeaveBack: () => this._pinContentSection(),
        // Hard fallback: once the header itself has scrolled out of view, guarantee the
        // pin is released even if `onEnter` never fired due to a mismeasured start point.
        onLeave: () => this._releaseContentSection(),
      }),
    );
  }

  // Pin the content section to the viewport (its default `fixed inset-0` state).
  _pinContentSection() {
    if (!this.hasContentSectionTarget) return;
    this.contentSectionTarget.style.position = "";
    this.contentSectionTarget.style.marginTop = "";
  }

  // Release the pin: switch to `relative` so the content flows with the header and
  // scrolls away. Idempotent — a no-op when already released. The margin compensates
  // for the layout shift so the content visually stays put at the moment of switching.
  _releaseContentSection() {
    if (!this.hasContentSectionTarget) return;
    if (this.contentSectionTarget.style.position === "relative") return;

    // Capture viewport position before switch (while still fixed)
    const before = this.contentSectionTarget.getBoundingClientRect();
    this.contentSectionTarget.style.position = "relative";
    // Measure the new position now that it's in flow
    const after = this.contentSectionTarget.getBoundingClientRect();
    // Difference = the offset we need to compensate to visually start at the same spot
    const offset = before.top - after.top;
    this.contentSectionTarget.style.marginTop = `${offset}px`;
  }

  _initializeContentLayout() {
    if (!this.hasContentTarget || !this.hasContentSectionTarget) return;

    const navElement = document.querySelector("#header-menu .menu-row");

    const update = () => {
      // Padding is computed by header_content_layout.js, which the header inlines as a
      // blocking script so the content is already in place on the first paint.
      const pushdown = window.headerContentLayout?.apply(this.element) ?? 0;

      // Pushdown is applied by GSAP (in _initializeCenterImage); only refresh when the value changes
      if (this._centerImagePushdown !== pushdown) {
        this._centerImagePushdown = pushdown;
        ScrollTrigger.refresh();
      }
    };

    this._layoutUpdate = () => requestAnimationFrame(update);

    if (this.hasCenterImageTarget) {
      const img = this.centerImageTarget.querySelector("img");
      if (img) {
        if (img.complete) {
          this._layoutUpdate();
        } else {
          img.addEventListener("load", this._layoutUpdate);
        }
      }
    }
    if (document.fonts?.ready) document.fonts.ready.then(this._layoutUpdate);

    window.addEventListener("load", this._layoutUpdate);
    window.addEventListener("resize", this._layoutUpdate);
    window.addEventListener("orientationchange", this._layoutUpdate);

    this._layoutObserver = new ResizeObserver(this._layoutUpdate);
    if (this.hasCenterImageTarget) this._layoutObserver.observe(this.centerImageTarget);
    this._layoutObserver.observe(this.contentTarget);
    this._layoutObserver.observe(this.contentSectionTarget);
    this._layoutObserver.observe(this.element);
    if (navElement) this._layoutObserver.observe(navElement);

    this._layoutUpdate();
  }

  _initializeMenuTransparencyToggle() {
    if (!this.hasHeaderBottomTarget) return;

    const mainMenu = document.getElementById("header-menu");
    if (!mainMenu) return;

    const menuRow = mainMenu.querySelector(".menu-row");
    const barHeight = () => {
      const token = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--menu-h-pinned"));
      return token || (menuRow ?? mainMenu).getBoundingClientRect().bottom;
    };

    this.scrollTriggers.push(
      ScrollTrigger.create({
        trigger: this.headerBottomTarget,
        start: () => `top ${barHeight()}px`,
        invalidateOnRefresh: true,
        onEnter: () => mainMenu.classList.remove("menu--transparent"),
        onLeaveBack: () => mainMenu.classList.add("menu--transparent"),
      }),
    );
  }
}
